import React, { useState, useMemo } from 'react';
import { Printer, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Award, CheckCircle, Users } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { calculateGrade, formatDate, formatCurrency } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import StatCard from '../../components/common/StatCard';

function Results() {
  const { exams, marks, students, classes, subjects, parents, staff, feePayments, feeStructures, routes,
    attendance, isResultPublished, publishResults, unpublishResults } = useApp();
  const { currentUser } = useAuth();

  const isStudentRole = currentUser?.role === 'student';
  const isParentRole = ['parent','father','mother'].includes(currentUser?.role);
  const isTeacher = currentUser?.role === 'teacher';
  const isAdmin = currentUser?.role === 'admin';
  const isRestricted = isStudentRole || isParentRole;
  const [publishing, setPublishing] = useState(false);
  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const availableClasses = isTeacher
    ? classes.filter(c => (teacherProfile?.classIds || []).includes(c.id))
    : classes;

  const [selectedExam, setSelectedExam] = useState(exams.find(e => e.status === 'completed')?.id || exams[0]?.id || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [view, setView] = useState('class'); // 'class' | 'subject'
  const [selectedIds, setSelectedIds] = useState(new Set());

  React.useEffect(() => {
    if (isStudentRole && currentUser?.profileId) {
      const s = students.find(st => st.id === currentUser.profileId);
      if (s) setSelectedClass(s.classId);
    }
  }, []);

  // Selection is scoped to whatever's currently on screen — clear it when
  // the exam/class filter changes so a stale selection can't silently
  // publish/unpublish students that are no longer visible.
  React.useEffect(() => { setSelectedIds(new Set()); }, [selectedExam, selectedClass]);

  const exam = exams.find(e => e.id === selectedExam);

  const gradeColor = (g) => {
    if (!g) return '#6b7280';
    if (g === 'A+') return '#16a34a';
    if (g === 'A')  return '#22c55e';
    if (g === 'B+') return '#2563eb';
    if (g === 'B')  return '#3b82f6';
    if (g === 'C+') return '#d97706';
    if (g === 'C')  return '#f59e0b';
    if (g === 'D')  return '#ea580c';
    return '#dc2626';
  };

  const barColor = (pct) => {
    if (pct >= 80) return '#16a34a';
    if (pct >= 60) return '#2563eb';
    if (pct >= 40) return '#f59e0b';
    return '#dc2626';
  };

  // Attendance % for one student on their report card — present days out
  // of all marked days, the same present/total convention already shown on
  // the parent/student dashboard, so this number matches what they already
  // see elsewhere. Scoped to the exam's academic year so it reads as "this
  // year", not the student's whole time at school. Null (not 0%) when no
  // attendance has been marked at all, so a remark isn't generated off data
  // that doesn't exist yet.
  const studentAttendancePct = (studentId, academicYear) => {
    const recs = attendance.filter(a => a.entityType === 'student' && a.entityId === studentId
      && (!academicYear || a.academicYear === academicYear));
    if (!recs.length) return null;
    const present = recs.filter(a => a.status === 'present').length;
    return Math.round((present / recs.length) * 100);
  };

  // A short, specific remark generated from the exam result and
  // attendance — every report card gets a comment tied to that student's
  // actual numbers instead of a blank box someone has to fill in by hand.
  const generateRemark = (pct, passed, attPct) => {
    const perf = !passed ? 'fail'
      : pct >= 90 ? 'outstanding'
      : pct >= 75 ? 'excellent'
      : pct >= 60 ? 'good'
      : 'satisfactory';
    const perfLine = {
      outstanding:  'Outstanding academic performance this term.',
      excellent:    'Excellent academic performance this term.',
      good:         'Good academic performance this term.',
      satisfactory: 'Satisfactory performance; there is scope for improvement.',
      fail:         'Performance needs significant improvement — extra attention and support are recommended.',
    }[perf];
    if (attPct == null) return perfLine;
    if (attPct < 75) return `${perfLine} Attendance of ${attPct}% is below the required level and may be affecting results — regular attendance is strongly encouraged.`;
    if (attPct >= 95) return `${perfLine} Attendance of ${attPct}% is excellent.`;
    return `${perfLine} Attendance of ${attPct}% is satisfactory.`;
  };

  // Reference grading scale printed on every report card — the same bands
  // calculateGrade() uses, kept alongside it here so the two can't drift.
  const gradingScaleRows = [
    ['A+', '90 – 100'], ['A', '80 – 89'], ['B+', '70 – 79'], ['B', '60 – 69'],
    ['C+', '50 – 59'], ['C', '40 – 49'], ['D', '33 – 39'], ['F', 'Below 33'],
  ];

  // Fee due for one student — tuition + transport combined, the same
  // formula the fee-pending gate below already uses to decide whether a
  // parent/student can even see this page, just returning the amount
  // instead of a yes/no. Staff need this alongside results to follow up on
  // both in one place; parents never see a nonzero figure here since the
  // gate already blocks them from this page while anything is outstanding.
  const studentFeeDue = (student) => {
    let due = 0;
    const clsName = classes.find(c => c.id === student.classId)?.name;
    const fs = feeStructures.find(f => f.classId === clsName || f.classId === student.classId);
    if (fs) {
      const tuitionPaid = feePayments.filter(p => p.studentId === student.id && p.type !== 'transportFee' && p.type !== 'admissionFee')
        .reduce((s, p) => s + Number(p.amount || 0) + Number(p.discount || 0), 0);
      const tuitionDue = Number(fs.totalAmount || 0) + Number(student.previousYearBalance || 0);
      due += Math.max(0, tuitionDue - tuitionPaid);
    }
    if (student.transportRouteId) {
      const route = routes.find(r => r.id === student.transportRouteId);
      if (route) {
        const allStops = route.stops || [];
        const stop = allStops.find(s => s.name === student.transportStop) || allStops.find(s => Number(s.fee) > 0);
        const annualFee = stop ? Number(stop.fee) : Number(route.fee || 0);
        if (annualFee > 0) {
          const transPaid = feePayments.filter(p => p.studentId === student.id && p.type === 'transportFee')
            .reduce((s, p) => s + Number(p.amount || 0) + Number(p.discount || 0), 0);
          due += Math.max(0, annualFee - transPaid);
        }
      }
    }
    return due;
  };

  // All marks for this exam
  const examMarks = marks.filter(m => m.examId === selectedExam);

  // Students in selected class (active)
  const classStudents = useMemo(() => {
    let list = students.filter(s => s.status === 'active');
    if (isStudentRole && currentUser?.profileId) return list.filter(s => s.id === currentUser.profileId);
    if (isParentRole && currentUser?.profileId) list = list.filter(s => s.parentId === currentUser.profileId);
    if (isTeacher) list = list.filter(s => availableClasses.some(c => c.id === s.classId));
    if (selectedClass) list = list.filter(s => s.classId === selectedClass);
    return list.sort((a, b) => (a.rollNumber || '').localeCompare(b.rollNumber || '', undefined, { numeric: true }));
  }, [students, selectedClass, isStudentRole, isParentRole, isTeacher, availableClasses, currentUser]);

  // Subjects that have marks entered for this exam + class, each carrying
  // its actual max marks for this class — GET /api/marks already resolves
  // that per class (a shared exam session can have Nursery out of 20 and
  // Class 8 out of 100 for the same subject), so every mark for the same
  // subject+class already carries the same value; the first one found is
  // as good as any other.
  const examSubjects = useMemo(() => {
    const maxBySubject = {};
    examMarks
      .filter(m => classStudents.some(s => s.id === m.studentId))
      .forEach(m => { if (maxBySubject[m.subjectId] == null) maxBySubject[m.subjectId] = m.maxMarks; });
    return Object.keys(maxBySubject)
      .map(id => {
        const sub = subjects.find(s => s.id === id);
        return sub ? { ...sub, maxMarks: maxBySubject[id] } : null;
      })
      .filter(Boolean);
  }, [examMarks, classStudents, subjects]);

  // Build per-student result rows
  const studentResults = useMemo(() => {
    return classStudents.map(s => {
      const sMarks = examMarks.filter(m => m.studentId === s.id);
      // Round to 2dp — summing decimal marks (e.g. 19.5 + 18.2 + ...) in
      // floating point otherwise produces values like 95.10000000000001.
      const total = Math.round(sMarks.reduce((sum, m) => sum + m.marksObtained, 0) * 100) / 100;
      // maxTotal is out of every subject assigned to this exam for the
      // class (examSubjects) — not just whichever subjects this particular
      // student happens to have a mark for. A student missing a subject
      // (absent, not yet graded) should show their real total out of the
      // exam's full possible marks, not a total that quietly shrinks to
      // match only what was entered — that would inflate their % next to
      // a classmate who was marked in every subject.
      const maxTotal = Math.round(examSubjects.reduce((sum, sub) => sum + sub.maxMarks, 0) * 100) / 100;
      const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
      const grade = maxTotal > 0 ? calculateGrade(total, maxTotal) : null;
      const passed = sMarks.length > 0 && sMarks.every(m => (m.marksObtained / m.maxMarks) >= 0.33);
      const subjectMap = {};
      sMarks.forEach(m => { subjectMap[m.subjectId] = m; });
      const parent = parents?.find(p => p.id === s.parentId);
      return { ...s, sMarks, total, maxTotal, pct, grade, passed, subjectMap, parent };
    }).map((s, _, arr) => {
      const rank = arr.filter(o => o.pct > s.pct).length + 1;
      return { ...s, rank };
    }).sort((a, b) => a.rank - b.rank);
  }, [classStudents, examMarks, parents, examSubjects]);

  // Subject-wise analysis
  const subjectResults = useMemo(() => {
    if (!selectedSubject) return [];
    return classStudents.map(s => {
      const m = examMarks.find(m => m.studentId === s.id && m.subjectId === selectedSubject);
      if (!m) return null;
      const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
      const grade = calculateGrade(m.marksObtained, m.maxMarks);
      return { ...s, marksObtained: m.marksObtained, maxMarks: m.maxMarks, pct, grade };
    }).filter(Boolean)
      .sort((a, b) => b.pct - a.pct)
      .map((s, i) => ({ ...s, rank: i + 1 }));
  }, [selectedSubject, classStudents, examMarks]);

  // ── Exam summary analytics (admin/teacher only — see gate on render) ──
  const classStudentIds = useMemo(() => new Set(classStudents.map(s => s.id)), [classStudents]);
  const classExamMarks = useMemo(() => examMarks.filter(m => classStudentIds.has(m.studentId)), [examMarks, classStudentIds]);
  // Students with zero marks entered are excluded from average/pass-rate
  // stats — "no data yet" isn't the same as "failed", same principle as
  // excluding approved Leave from the attendance percentage.
  const studentsWithMarks = useMemo(() => studentResults.filter(s => s.sMarks.length > 0), [studentResults]);

  const classAvgPct = useMemo(() => {
    if (!classExamMarks.length) return null;
    const obtained = classExamMarks.reduce((s, m) => s + m.marksObtained, 0);
    const max = classExamMarks.reduce((s, m) => s + m.maxMarks, 0);
    return max > 0 ? Math.round((obtained / max) * 100) : null;
  }, [classExamMarks]);

  const passCount = useMemo(() => studentsWithMarks.filter(s => s.passed).length, [studentsWithMarks]);
  const passPct = studentsWithMarks.length ? Math.round((passCount / studentsWithMarks.length) * 100) : null;

  const highestStudent = useMemo(() =>
    studentsWithMarks.length ? studentsWithMarks.reduce((a, b) => (b.pct > a.pct ? b : a)) : null,
    [studentsWithMarks]);

  // Top Performers — every student who scored 90% or above, not just a
  // fixed top-5, so a class with six 90%+ students shows all six.
  const topPerformers = useMemo(() =>
    studentsWithMarks.filter(s => s.pct >= 90).sort((a, b) => b.pct - a.pct),
    [studentsWithMarks]);

  // Attention Required — students below 45%, the ones most likely to need
  // extra support, sorted lowest-first so the most urgent case is on top.
  const attentionRequired = useMemo(() =>
    studentsWithMarks.filter(s => s.pct < 45).sort((a, b) => a.pct - b.pct),
    [studentsWithMarks]);

  // Sorted weakest-first, same convention as the Attendance class-comparison
  // chart — the subject that needs attention should be immediately visible.
  const subjectAverages = useMemo(() => {
    const bySubject = {};
    classExamMarks.forEach(m => { (bySubject[m.subjectId] = bySubject[m.subjectId] || []).push(m); });
    return examSubjects.map(sub => {
      const ms = bySubject[sub.id] || [];
      const obtained = ms.reduce((s, m) => s + m.marksObtained, 0);
      const max = ms.reduce((s, m) => s + m.maxMarks, 0);
      return { subjectId: sub.id, name: sub.name, avgPct: max > 0 ? Math.round((obtained / max) * 100) : 0, hasData: ms.length > 0 };
    }).filter(d => d.hasData).sort((a, b) => a.avgPct - b.avgPct);
  }, [classExamMarks, examSubjects]);

  // Class average % per exam over time, for the same class/role scope —
  // lets admin/teacher see whether this class is improving exam-to-exam.
  const examTrendData = useMemo(() => {
    return exams.map(ex => {
      const exMarks = marks.filter(m => m.examId === ex.id && classStudentIds.has(m.studentId));
      if (!exMarks.length) return null;
      const obtained = exMarks.reduce((s, m) => s + m.marksObtained, 0);
      const max = exMarks.reduce((s, m) => s + m.maxMarks, 0);
      const pct = max > 0 ? Math.round((obtained / max) * 100) : null;
      if (pct === null) return null;
      return { examId: ex.id, name: ex.name, pct, date: ex.startDate || '' };
    }).filter(Boolean).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [exams, marks, classStudentIds]);

  const currentExamTrendIdx = examTrendData.findIndex(d => d.examId === selectedExam);
  const trendDelta = (currentExamTrendIdx > 0 && classAvgPct !== null)
    ? classAvgPct - examTrendData[currentExamTrendIdx - 1].pct
    : null;

  // Print individual report card
  const printReportCard = async (sr) => {
    const school = await getSchoolInfo();
    const cls = classes.find(c => c.id === sr.classId);
    const totalMarks = sr.total;
    const totalMax = sr.maxTotal;
    const overallPct = sr.pct;
    const overallGrade = sr.grade;
    const gradeBg = overallGrade ? gradeColor(overallGrade.grade) : '#6b7280';
    const resultLabel = sr.passed ? 'PASS' : 'FAIL';
    const resultColor = sr.passed ? '#16a34a' : '#dc2626';
    const attPct = studentAttendancePct(sr.id, exam?.academicYear);
    const remark = generateRemark(overallPct, sr.passed, attPct);
    const scaleRows = gradingScaleRows.map(([g, range]) =>
      `<tr><td class="center grade" style="color:${gradeColor(g)}">${g}</td><td class="center">${range}</td></tr>`).join('');

    const subjectRows = examSubjects.map(sub => {
      const m = sr.subjectMap[sub.id];
      if (!m) return '';
      const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
      const grade = calculateGrade(m.marksObtained, m.maxMarks);
      return `<tr>
        <td>${sub.name}</td>
        <td class="center">${m.maxMarks}</td>
        <td class="center"><strong>${m.marksObtained}</strong></td>
        <td class="center">${pct}%</td>
        <td class="center"><div class="bar-wrap"><div class="bar" style="width:${pct}%;background:${barColor(pct)}"></div></div></td>
        <td class="center grade" style="color:${gradeColor(grade.grade)}">${grade.grade}</td>
        <td>${m.remarks || '—'}</td>
      </tr>`;
    }).join('');

    const win = window.open('', '_blank', 'width=900,height=800');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Report Card — ${sr.firstName} ${sr.lastName}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; background:#f0f4f8; color:#1e293b; font-size:13px; }
  .page { max-width:860px; margin:24px auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.12); }
  .student-strip { display:flex; align-items:center; gap:20px; background:#f8fafc; border-bottom:1px solid #e2e8f0; padding:14px 20px; }
  .avatar { width:48px; height:48px; border-radius:50%; background:linear-gradient(135deg,#6b21a8,#9333ea); color:#fff; display:flex; align-items:center; justify-content:center; font-size:18px; font-weight:700; flex-shrink:0; }
  .s-name { font-size:16px; font-weight:700; color:#0f172a; }
  .s-meta { font-size:12px; color:#64748b; margin-top:2px; }
  .s-details { display:flex; gap:24px; margin-left:auto; }
  .lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
  .val { font-size:12px; font-weight:600; color:#1e293b; margin-top:1px; }
  .score-section { display:flex; border-bottom:1px solid #e2e8f0; }
  .score-box { flex:1; padding:12px 16px; text-align:center; border-right:1px solid #e2e8f0; }
  .score-box:last-child { border-right:none; }
  .s-lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
  .s-val { font-size:22px; font-weight:900; }
  .s-sub { font-size:11px; color:#94a3b8; margin-top:2px; }
  .grade-circle { display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; font-size:18px; font-weight:900; color:#fff; }
  .table-wrap { padding:14px 20px 18px; }
  .section-title { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; margin-bottom:10px; }
  table { width:100%; border-collapse:collapse; }
  thead tr { background:#f1f5f9; }
  thead th { padding:8px 10px; font-size:11px; font-weight:700; color:#475569; text-align:left; border-bottom:2px solid #e2e8f0; }
  thead th.center { text-align:center; }
  tbody tr { border-bottom:1px solid #f1f5f9; }
  td { padding:8px 10px; font-size:12px; color:#334155; }
  td.center { text-align:center; }
  td.grade { font-size:14px; font-weight:800; }
  .bar-wrap { width:60px; height:6px; background:#e2e8f0; border-radius:99px; margin:0 auto; overflow:hidden; }
  .bar { height:100%; border-radius:99px; }
  tfoot tr { background:#f8fafc; border-top:2px solid #e2e8f0; }
  tfoot td { padding:10px; font-weight:700; font-size:13px; color:#0f172a; }
  .bottom-grid { display:flex; gap:16px; padding:0 20px 16px; }
  .scale-box { width:170px; flex-shrink:0; }
  .scale-box table { border:1px solid #e2e8f0; border-radius:8px; overflow:hidden; }
  .scale-box th, .scale-box td { padding:4px 8px; font-size:11px; }
  .scale-box thead th { border-bottom:1px solid #e2e8f0; }
  .scale-box tbody tr:not(:last-child) td { border-bottom:1px solid #f1f5f9; }
  .remark-box { flex:1; background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:10px 14px; }
  .remark-box p { font-size:12px; line-height:1.6; color:#334155; }
  .card-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 20px; background:#f8fafc; border-top:1px solid #e2e8f0; }
  .sig-line { width:120px; border-top:1px solid #94a3b8; padding-top:4px; font-size:10px; color:#94a3b8; text-align:center; }
  .print-btn { text-align:center; padding:14px; background:#f0f4f8; }
  @media print { .print-btn{display:none;} body{background:#fff;} .page{box-shadow:none;margin:0;border-radius:0;} *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color:#000!important;} img{display:block!important;} }
</style></head><body>
<div class="page">
  <div style="padding:14px 20px 0">
    ${schoolHeader(school)}
    <div style="text-align:center;font-size:14px;font-weight:700;color:#374151;margin:-6px 0 10px;text-transform:uppercase">Report Card &mdash; ${exam?.name || 'Examination'} &mdash; ${exam?.academicYear || '2025-2026'}</div>
  </div>
  <div class="student-strip">
    <div class="avatar">${sr.firstName?.[0]}${sr.lastName?.[0]}</div>
    <div><div class="s-name">${sr.firstName} ${sr.lastName}</div><div class="s-meta">${cls ? `${cls.name}-${cls.section}` : ''} &nbsp;·&nbsp; Roll No: ${sr.rollNumber || '—'}</div></div>
    <div class="s-details">
      <div><div class="lbl">Adm No</div><div class="val">${sr.admissionNumber || '—'}</div></div>
      <div><div class="lbl">Parent</div><div class="val">${sr.parent?.name || '—'}</div></div>
      <div><div class="lbl">Phone</div><div class="val">${sr.parent?.phone || '—'}</div></div>
      <div><div class="lbl">Rank</div><div class="val">${sr.rank}</div></div>
    </div>
  </div>
  <div class="score-section">
    <div class="score-box"><div class="s-lbl">Total Marks</div><div class="s-val" style="color:#6b21a8">${totalMarks} / ${totalMax}</div><div class="s-sub">Subjects: ${examSubjects.length}</div></div>
    <div class="score-box"><div class="s-lbl">Percentage</div><div class="s-val" style="color:${barColor(overallPct)}">${overallPct}%</div><div class="s-sub">${overallPct >= 75 ? 'Distinction' : overallPct >= 60 ? 'First Class' : overallPct >= 50 ? 'Second Class' : overallPct >= 33 ? 'Pass' : 'Fail'}</div></div>
    <div class="score-box"><div class="s-lbl">Overall Grade</div><div style="margin-top:2px"><span class="grade-circle" style="background:${gradeBg}">${overallGrade?.grade || '—'}</span></div><div class="s-sub">Grade Points: ${overallGrade?.gp ?? '—'}</div></div>
    <div class="score-box"><div class="s-lbl">Attendance</div><div class="s-val" style="color:${attPct == null ? '#94a3b8' : attPct < 75 ? '#dc2626' : '#16a34a'}">${attPct == null ? '—' : attPct + '%'}</div><div class="s-sub">${exam?.academicYear || 'This year'}</div></div>
    <div class="score-box"><div class="s-lbl">Result</div><div class="s-val" style="color:${resultColor}">${resultLabel}</div><div class="s-sub">${formatDate(new Date().toISOString().split('T')[0])}</div></div>
  </div>
  <div class="table-wrap">
    <div class="section-title">Subject-wise Performance</div>
    <table>
      <thead><tr><th>Subject</th><th class="center">Max</th><th class="center">Obtained</th><th class="center">%</th><th class="center">Progress</th><th class="center">Grade</th><th>Remarks</th></tr></thead>
      <tbody>${subjectRows}</tbody>
      <tfoot><tr>
        <td>Grand Total</td><td class="center">${totalMax}</td><td class="center">${totalMarks}</td><td class="center">${overallPct}%</td>
        <td class="center"><div class="bar-wrap"><div class="bar" style="width:${overallPct}%;background:${barColor(overallPct)}"></div></div></td>
        <td class="center grade" style="color:${gradeBg}">${overallGrade?.grade || '—'}</td><td></td>
      </tr></tfoot>
    </table>
  </div>
  <div class="bottom-grid">
    <div class="scale-box">
      <div class="section-title">Grading Scale</div>
      <table><thead><tr><th class="center">Grade</th><th class="center">%</th></tr></thead><tbody>${scaleRows}</tbody></table>
    </div>
    <div class="remark-box">
      <div class="section-title">Teacher's Remark</div>
      <p>${remark}</p>
    </div>
  </div>
  <div class="card-footer">
    <div class="sig-line">Class Teacher</div>
    <div style="font-size:10px;color:#94a3b8;text-align:center">Computer-generated report card<br/>${school.name}</div>
    <div class="sig-line">Principal</div>
  </div>
</div>
<div class="print-btn">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#6b21a8,#9333ea);color:#fff;border:none;padding:12px 36px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Roboto',sans-serif">&#128424;&nbsp; Print Report Card</button>
</div>
</body></html>`); win.document.close();
  };

  // Print all students report cards (one per page)
  const printAllReportCards = async () => {
    const school = await getSchoolInfo();
    const allPages = studentResults.map(sr => {
      const cls = classes.find(c => c.id === sr.classId);
      const totalMarks = sr.total;
      const totalMax = sr.maxTotal;
      const overallPct = sr.pct;
      const overallGrade = sr.grade;
      const gradeBg = overallGrade ? gradeColor(overallGrade.grade) : '#6b7280';
      const resultLabel = sr.passed ? 'PASS' : 'FAIL';
      const resultColor = sr.passed ? '#16a34a' : '#dc2626';
      const attPct = studentAttendancePct(sr.id, exam?.academicYear);
      const remark = generateRemark(overallPct, sr.passed, attPct);
      const scaleRows = gradingScaleRows.map(([g, range]) =>
        `<tr><td class="center grade" style="color:${gradeColor(g)}">${g}</td><td class="center">${range}</td></tr>`).join('');

      const subjectRows = examSubjects.map(sub => {
        const m = sr.subjectMap[sub.id];
        if (!m) return '';
        const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
        const grade = calculateGrade(m.marksObtained, m.maxMarks);
        return `<tr>
          <td>${sub.name}</td>
          <td class="center">${m.maxMarks}</td>
          <td class="center"><strong>${m.marksObtained}</strong></td>
          <td class="center">${pct}%</td>
          <td class="center"><div class="bar-wrap"><div class="bar" style="width:${pct}%;background:${barColor(pct)}"></div></div></td>
          <td class="center grade" style="color:${gradeColor(grade.grade)}">${grade.grade}</td>
          <td>${m.remarks || '—'}</td>
        </tr>`;
      }).join('');

      return `
      <div class="page">
        <div style="padding:14px 20px 0">
          ${schoolHeader(school)}
          <div style="text-align:center;font-size:14px;font-weight:700;color:#374151;margin:-6px 0 10px;text-transform:uppercase">Report Card &mdash; ${exam?.name || 'Examination'} &mdash; ${exam?.academicYear || '2025-2026'}</div>
        </div>
        <div class="student-strip">
          <div class="avatar">${sr.firstName?.[0]}${sr.lastName?.[0]}</div>
          <div><div class="s-name">${sr.firstName} ${sr.lastName}</div><div class="s-meta">${cls ? `${cls.name}-${cls.section}` : ''} &nbsp;·&nbsp; Roll No: ${sr.rollNumber || '—'}</div></div>
          <div class="s-details">
            <div><div class="lbl">Adm No</div><div class="val">${sr.admissionNumber || '—'}</div></div>
            <div><div class="lbl">Parent</div><div class="val">${sr.parent?.name || '—'}</div></div>
            <div><div class="lbl">Phone</div><div class="val">${sr.parent?.phone || '—'}</div></div>
            <div><div class="lbl">Rank</div><div class="val">${sr.rank}</div></div>
          </div>
        </div>
        <div class="score-section">
          <div class="score-box"><div class="s-lbl">Total Marks</div><div class="s-val" style="color:#6b21a8">${totalMarks} / ${totalMax}</div><div class="s-sub">Subjects: ${examSubjects.length}</div></div>
          <div class="score-box"><div class="s-lbl">Percentage</div><div class="s-val" style="color:${barColor(overallPct)}">${overallPct}%</div><div class="s-sub">${overallPct >= 75 ? 'Distinction' : overallPct >= 60 ? 'First Class' : overallPct >= 50 ? 'Second Class' : overallPct >= 33 ? 'Pass' : 'Fail'}</div></div>
          <div class="score-box"><div class="s-lbl">Overall Grade</div><div style="margin-top:2px"><span class="grade-circle" style="background:${gradeBg}">${overallGrade?.grade || '—'}</span></div><div class="s-sub">Grade Points: ${overallGrade?.gp ?? '—'}</div></div>
          <div class="score-box"><div class="s-lbl">Attendance</div><div class="s-val" style="color:${attPct == null ? '#94a3b8' : attPct < 75 ? '#dc2626' : '#16a34a'}">${attPct == null ? '—' : attPct + '%'}</div><div class="s-sub">${exam?.academicYear || 'This year'}</div></div>
          <div class="score-box"><div class="s-lbl">Result</div><div class="s-val" style="color:${resultColor}">${resultLabel}</div><div class="s-sub">${formatDate(new Date().toISOString().split('T')[0])}</div></div>
        </div>
        <div class="table-wrap">
          <div class="section-title">Subject-wise Performance</div>
          <table>
            <thead><tr><th>Subject</th><th class="center">Max</th><th class="center">Obtained</th><th class="center">%</th><th class="center">Progress</th><th class="center">Grade</th><th>Remarks</th></tr></thead>
            <tbody>${subjectRows}</tbody>
            <tfoot><tr>
              <td>Grand Total</td><td class="center">${totalMax}</td><td class="center">${totalMarks}</td><td class="center">${overallPct}%</td>
              <td class="center"><div class="bar-wrap"><div class="bar" style="width:${overallPct}%;background:${barColor(overallPct)}"></div></div></td>
              <td class="center grade" style="color:${gradeBg}">${overallGrade?.grade || '—'}</td><td></td>
            </tr></tfoot>
          </table>
        </div>
        <div class="bottom-grid">
          <div class="scale-box">
            <div class="section-title">Grading Scale</div>
            <table><thead><tr><th class="center">Grade</th><th class="center">%</th></tr></thead><tbody>${scaleRows}</tbody></table>
          </div>
          <div class="remark-box">
            <div class="section-title">Teacher's Remark</div>
            <p>${remark}</p>
          </div>
        </div>
        <div class="card-footer">
          <div class="sig-line">Class Teacher</div>
          <div style="font-size:10px;color:#94a3b8;text-align:center">Computer-generated report card<br/>${school.name}</div>
          <div class="sig-line">Principal</div>
        </div>
      </div>`;
    }).join('');

    const win = window.open('', '_blank', 'width=960,height=800');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Report Cards — ${exam?.name || 'Examination'}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; background:#e5e7eb; color:#1e293b; font-size:13px; }
  .page { max-width:860px; margin:24px auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.12); page-break-after:always; }
  .page:last-child { page-break-after:auto; }
  .student-strip { display:flex; align-items:center; gap:20px; background:#f8fafc; border-bottom:1px solid #e2e8f0; padding:14px 20px; }
  .avatar { width:48px; height:48px; border-radius:50%; background:linear-gradient(135deg,#6b21a8,#9333ea); color:#fff; display:flex; align-items:center; justify-content:center; font-size:18px; font-weight:700; flex-shrink:0; }
  .s-name { font-size:16px; font-weight:700; color:#0f172a; }
  .s-meta { font-size:12px; color:#64748b; margin-top:2px; }
  .s-details { display:flex; gap:24px; margin-left:auto; }
  .lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
  .val { font-size:12px; font-weight:600; color:#1e293b; margin-top:1px; }
  .score-section { display:flex; border-bottom:1px solid #e2e8f0; }
  .score-box { flex:1; padding:12px 16px; text-align:center; border-right:1px solid #e2e8f0; }
  .score-box:last-child { border-right:none; }
  .s-lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
  .s-val { font-size:22px; font-weight:900; }
  .s-sub { font-size:11px; color:#94a3b8; margin-top:2px; }
  .grade-circle { display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; font-size:18px; font-weight:900; color:#fff; }
  .table-wrap { padding:14px 20px 18px; }
  .section-title { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; margin-bottom:10px; }
  table { width:100%; border-collapse:collapse; }
  thead tr { background:#f1f5f9; }
  thead th { padding:8px 10px; font-size:11px; font-weight:700; color:#475569; text-align:left; border-bottom:2px solid #e2e8f0; }
  thead th.center { text-align:center; }
  tbody tr { border-bottom:1px solid #f1f5f9; }
  td { padding:8px 10px; font-size:12px; color:#334155; }
  td.center { text-align:center; }
  td.grade { font-size:14px; font-weight:800; }
  .bar-wrap { width:60px; height:6px; background:#e2e8f0; border-radius:99px; margin:0 auto; overflow:hidden; }
  .bar { height:100%; border-radius:99px; }
  tfoot tr { background:#f8fafc; border-top:2px solid #e2e8f0; }
  tfoot td { padding:10px; font-weight:700; font-size:13px; color:#0f172a; }
  .bottom-grid { display:flex; gap:16px; padding:0 20px 16px; }
  .scale-box { width:170px; flex-shrink:0; }
  .scale-box table { border:1px solid #e2e8f0; border-radius:8px; overflow:hidden; }
  .scale-box th, .scale-box td { padding:4px 8px; font-size:11px; }
  .scale-box thead th { border-bottom:1px solid #e2e8f0; }
  .scale-box tbody tr:not(:last-child) td { border-bottom:1px solid #f1f5f9; }
  .remark-box { flex:1; background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:10px 14px; }
  .remark-box p { font-size:12px; line-height:1.6; color:#334155; }
  .card-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 20px; background:#f8fafc; border-top:1px solid #e2e8f0; }
  .sig-line { width:120px; border-top:1px solid #94a3b8; padding-top:4px; font-size:10px; color:#94a3b8; text-align:center; }
  .print-bar { text-align:center; padding:16px; background:#e5e7eb; position:sticky; top:0; z-index:99; }
  @media print {
    .print-bar { display:none; }
    body { background:#fff; }
    .page { box-shadow:none; margin:0; border-radius:0; }
    * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; color:#000!important; }
    img { display:block!important; }
  }
</style></head><body>
<div class="print-bar">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#6b21a8,#9333ea);color:#fff;border:none;padding:12px 36px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Roboto',sans-serif;box-shadow:0 4px 12px rgba(107,33,168,0.3)">
    &#128424;&nbsp; Print All ${studentResults.length} Report Cards
  </button>
  <span style="margin-left:16px;font-size:13px;color:#555">${exam?.name} — ${classes.find(c => c.id === selectedClass) ? `${classes.find(c => c.id === selectedClass).name}-${classes.find(c => c.id === selectedClass).section}` : 'All Classes'} — ${studentResults.length} students</span>
</div>
${allPages}
</body></html>`); win.document.close();
  };

  // Print class result sheet
  const printClassSheet = async () => {
    const school = await getSchoolInfo();
    const cls = classes.find(c => c.id === selectedClass);
    const headerCols = examSubjects.map(s => `<th class="center">${s.name}</th>`).join('');
    const rows = studentResults.map((sr) => {
      const subCols = examSubjects.map(sub => {
        const m = sr.subjectMap[sub.id];
        return m ? `<td class="center">${m.marksObtained}/${m.maxMarks}</td>` : `<td class="center">—</td>`;
      }).join('');
      return `<tr>
        <td class="center">${sr.rank}</td>
        <td>${sr.firstName} ${sr.lastName}</td>
        <td class="center">${sr.rollNumber || '—'}</td>
        ${subCols}
        <td class="center"><strong>${sr.total}/${sr.maxTotal}</strong></td>
        <td class="center">${sr.pct}%</td>
        <td class="center" style="color:${gradeColor(sr.grade?.grade)};font-weight:700">${sr.grade?.grade || '—'}</td>
        <td class="center" style="color:${sr.passed ? '#16a34a' : '#dc2626'};font-weight:700">${sr.passed ? 'P' : 'F'}</td>
      </tr>`;
    }).join('');

    const win = window.open('', '_blank', 'width=1100,height=700');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Class Result Sheet</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; padding:24px 28px; color:#111; font-size:12px; }
  .school-header { margin-bottom:16px; }
  .meta { display:flex; justify-content:space-between; margin-bottom:12px; font-size:12px; }
  .meta .title { font-size:15px; font-weight:700; color:#6b21a8; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 8px; text-align:left; border-bottom:2px solid #d1d5db; white-space:nowrap; }
  thead th.center { text-align:center; }
  td { padding:6px 8px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
  td.center { text-align:center; }
  tr:nth-child(even) { background:#fafafa; }
  .footer { text-align:center; font-size:11px; color:#aaa; margin-top:20px; padding-top:8px; border-top:1px dashed #e5e7eb; }
  .print-btn { text-align:center; margin-top:14px; }
  @page { margin: 3mm; } @media print { .print-btn{display:none;} body{padding:4px;} * { color:#000 !important; } }
</style></head><body>
<div class="school-header">
  ${schoolHeader(school, '#6b21a8', `Class Result Sheet &mdash; ${exam?.name || 'Examination'}`)}
</div>
<div class="meta">
  <div><div class="title">Class: ${cls ? `${cls.name}-${cls.section}` : 'All Classes'}</div><div style="color:#555;margin-top:2px">${studentResults.length} students</div></div>
  <div style="text-align:right;color:#555">Date: <strong>${formatDate(new Date().toISOString().split('T')[0])}</strong></div>
</div>
<table>
  <thead><tr><th class="center">Rank</th><th>Student</th><th class="center">Roll</th>${headerCols}<th class="center">Total</th><th class="center">%</th><th class="center">Grade</th><th class="center">Result</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Computer-generated &mdash; ${school.name}</div>
<div class="print-btn"><button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',sans-serif">&#128424; Print</button></div>
</body></html>`); win.document.close();
  };

  const selSubjectObj = subjects.find(s => s.id === selectedSubject);

  // Fee-pending gate for parent / student roles
  if (isParentRole || isStudentRole) {
    const checkStudents = isStudentRole
      ? students.filter(s => s.id === currentUser?.profileId)
      : students.filter(s => (s.parentId === currentUser?.profileId || s.id === currentUser?.profileId));

    const hasPendingFee = checkStudents.some(student => {
      const studentClassName = classes.find(c => c.id === student.classId)?.name;
      const fs = feeStructures.find(f => f.classId === studentClassName);
      if (fs) {
        const tuitionPaid = feePayments.filter(p => p.studentId === student.id && p.type !== 'transportFee').reduce((s, p) => s + Number(p.amount || 0) + Number(p.discount || 0), 0);
        const tuitionDue  = Number(fs.totalAmount || 0) + Number(student.previousYearBalance || 0);
        if (tuitionPaid < tuitionDue) return true;
      }
      if (student.transportRouteId) {
        const route = routes.find(r => r.id === student.transportRouteId);
        if (route) {
          const allStops = route.stops || [];
          const stop = allStops.find(s => s.name === student.transportStop) || allStops.find(s => Number(s.fee) > 0);
          const annualFee = stop ? Number(stop.fee) : Number(route.fee || 0);
          if (annualFee > 0) {
            const transPaid = feePayments.filter(p => p.studentId === student.id && p.type === 'transportFee').reduce((s, p) => s + Number(p.amount || 0) + Number(p.discount || 0), 0);
            if (transPaid < annualFee) return true;
          }
        }
      }
      return false;
    });

    if (hasPendingFee) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-10 text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-red-700 mb-2">Fee Payment Pending</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Please contact the <strong>Principal</strong> for the results of your {isParentRole ? 'child' : 'account'}.
            </p>
          </div>
        </div>
      );
    }

    // Results only become visible to a parent/student once an admin has
    // published that specific student's results for this exam (see the
    // per-student selection + Publish button in the admin view below).
    const notPublished = selectedExam && checkStudents.length > 0 &&
      !checkStudents.some(student => isResultPublished(selectedExam, student.id));
    if (notPublished) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center max-w-md">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-gray-700 mb-2">Please contact School Admin/Principal</h2>
          </div>
        </div>
      );
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Results</h1>
        <div className="flex items-center gap-2">
          {!isRestricted && (
            <>
              <button onClick={printAllReportCards} disabled={studentResults.length === 0}
                className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <Printer size={14}/> Print All Report Cards
              </button>
              <button onClick={printClassSheet} disabled={studentResults.length === 0}
                className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <Printer size={14}/> Print Class Sheet
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div>
          <label className="block font-medium mb-1">Exam</label>
          <select value={selectedExam} onChange={e => setSelectedExam(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {!isRestricted && (
          <div>
            <label className="block font-medium mb-1">Class</label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setExpandedStudent(null); }}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              <option value="">All Classes</option>
              {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
            </select>
          </div>
        )}
        {isAdmin && selectedExam && selectedIds.size > 0 && (
          <div>
            <label className="block font-medium mb-1">Parent Visibility ({selectedIds.size} selected)</label>
            <div className="flex gap-2">
              <button
                disabled={publishing}
                onClick={async () => {
                  setPublishing(true);
                  await publishResults(selectedExam, Array.from(selectedIds));
                  setPublishing(false);
                  setSelectedIds(new Set());
                }}
                className="px-3 py-1 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {publishing ? 'Working…' : 'Publish Selected'}
              </button>
              <button
                disabled={publishing}
                onClick={async () => {
                  setPublishing(true);
                  await unpublishResults(selectedExam, Array.from(selectedIds));
                  setPublishing(false);
                  setSelectedIds(new Set());
                }}
                className="px-3 py-1 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50">
                Unpublish Selected
              </button>
            </div>
          </div>
        )}
        <div>
          <label className="block font-medium mb-1">Subject</label>
          <select value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setView(e.target.value ? 'subject' : 'class'); }}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            <option value="">All Subjects</option>
            {examSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {/* View toggle */}
        <div className="flex border border-gray-300 rounded-lg overflow-hidden ml-2">
          <button onClick={() => { setView('class'); setSelectedSubject(''); }}
            className={`px-3 py-1 text-sm ${view === 'class' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}>
            Class View
          </button>
          <button onClick={() => setView('subject')}
            className={`px-3 py-1 text-sm border-l border-gray-300 ${view === 'subject' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}>
            Subject View
          </button>
        </div>
      </div>

      {/* Exam summary analytics — admin/teacher only; a parent/student's
          classStudents is already scoped to just their own child, so a
          "class average" chart here would be misleading, not informative. */}
      {!isRestricted && selectedExam && (
        <div className="space-y-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={trendDelta === null ? TrendingUp : trendDelta >= 0 ? TrendingUp : TrendingDown}
              label="Class Average"
              value={classAvgPct === null ? '—' : `${classAvgPct}%`}
              subtext={trendDelta === null ? 'No prior exam to compare' : `${trendDelta >= 0 ? '▲' : '▼'} ${Math.abs(trendDelta)} pts vs last exam`}
              colorClass={classAvgPct === null ? 'bg-gray-400' : classAvgPct >= 60 ? 'bg-green-500' : 'bg-orange-500'} />
            <StatCard icon={CheckCircle} label="Pass Rate"
              value={passPct === null ? '—' : `${passPct}%`}
              subtext={studentsWithMarks.length ? `${passCount}/${studentsWithMarks.length} passed` : 'No marks entered yet'}
              colorClass={passPct === null ? 'bg-gray-400' : passPct >= 75 ? 'bg-green-500' : 'bg-red-500'} />
            <StatCard icon={Award} label="Highest Score"
              value={highestStudent ? `${highestStudent.pct}%` : '—'}
              subtext={highestStudent ? `${highestStudent.firstName} ${highestStudent.lastName}` : 'No marks yet'}
              colorClass="bg-purple-500" />
            <StatCard icon={Users} label="Students Assessed"
              value={studentsWithMarks.length}
              subtext={`of ${classStudents.length} in view`}
              colorClass="bg-indigo-500" />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold mb-3">Subject-wise Average</h3>
            {subjectAverages.length === 0 ? (
              <p className="text-gray-400 text-sm py-16 text-center">No marks entered yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, subjectAverages.length * 34)}>
                <BarChart data={subjectAverages} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip formatter={v => `${v}%`} />
                  <ReferenceLine x={33} stroke="#9ca3af" strokeDasharray="4 4" label={{ value: 'Pass', fontSize: 10, fill: '#9ca3af', position: 'insideTopLeft' }} />
                  <Bar dataKey="avgPct" name="Average %" radius={[0, 4, 4, 0]}>
                    {subjectAverages.map(d => <Cell key={d.subjectId} fill={d.avgPct < 33 ? '#dc2626' : d.avgPct < 60 ? '#f59e0b' : '#16a34a'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-1.5"><Award size={16} className="text-amber-500" /> Top Performers <span className="text-xs font-normal text-gray-400">(90%+)</span></h3>
              {topPerformers.length === 0 ? (
                <p className="text-gray-400 text-sm py-16 text-center">No student has scored 90% or above yet.</p>
              ) : (
                <div>
                  {topPerformers.map((s, i) => (
                    <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                      <span className="w-6 text-center font-bold" style={{ color: i === 0 ? '#d97706' : i === 1 ? '#6b7280' : i === 2 ? '#92400e' : '#9ca3af' }}>{i + 1}</span>
                      <span className="flex-1 text-sm font-medium truncate">{s.firstName} {s.lastName}</span>
                      <span className="text-sm font-bold" style={{ color: barColor(s.pct) }}>{s.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-1.5"><TrendingDown size={16} className="text-red-500" /> Attention Required <span className="text-xs font-normal text-gray-400">(below 45%)</span></h3>
              {attentionRequired.length === 0 ? (
                <p className="text-gray-400 text-sm py-16 text-center">No student is currently below 45%.</p>
              ) : (
                <div>
                  {attentionRequired.map(s => (
                    <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                      <span className="flex-1 text-sm font-medium truncate">{s.firstName} {s.lastName}</span>
                      <span className="text-sm font-bold text-red-600">{s.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold mb-3">Exam-over-Exam Trend</h3>
              {examTrendData.length < 2 ? (
                <p className="text-gray-400 text-sm py-16 text-center">Need at least 2 exams with marks to show a trend.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={examTrendData} margin={{ left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                    <Tooltip formatter={v => `${v}%`} />
                    <Line type="monotone" dataKey="pct" name="Class Average %" stroke="#6b21a8" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -- CLASS VIEW -- */}
      {view === 'class' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {studentResults.length === 0 ? (
            <div className="py-12 text-center">Select a class and exam to view results.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {isAdmin && (
                    <th className="px-3 py-2 text-center font-semibold w-8">
                      <input type="checkbox"
                        checked={studentResults.length > 0 && studentResults.every(sr => selectedIds.has(sr.id))}
                        onChange={e => setSelectedIds(e.target.checked
                          ? new Set(studentResults.map(sr => sr.id))
                          : new Set())} />
                    </th>
                  )}
                  <th className="px-3 py-2 text-center font-semibold w-12">Rank</th>
                  <th className="px-4 py-2 text-left font-semibold">Student</th>
                  <th className="px-3 py-2 text-center font-semibold">Roll</th>
                  {examSubjects.map(s => (
                    // Subject code instead of the full name — this row of
                    // columns gets wide fast with 6+ subjects; falls back to
                    // the name for any subject that's never had a code set.
                    <th key={s.id} className="px-3 py-2 text-center font-semibold whitespace-nowrap" title={s.name}>{s.code || s.name}</th>
                  ))}
                  <th className="px-3 py-2 text-center font-semibold">Total</th>
                  <th className="px-3 py-2 text-center font-semibold">%</th>
                  <th className="px-3 py-2 text-center font-semibold">Grade</th>
                  <th className="px-3 py-2 text-center font-semibold">Result</th>
                  {/* Admin-only — not shown to teachers, and not printed on
                      the report card. */}
                  {isAdmin && <th className="px-3 py-2 text-center font-semibold">Fee Due</th>}
                  {isAdmin && <th className="px-3 py-2 text-center font-semibold">Parent Visibility</th>}
                  {!isParentRole && <th className="px-3 py-2 text-center font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {studentResults.map(sr => (
                  <React.Fragment key={sr.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedStudent(expandedStudent === sr.id ? null : sr.id)}>
                      {isAdmin && (
                        <td className="px-3 py-1 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(sr.id)}
                            onChange={e => setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(sr.id); else next.delete(sr.id);
                              return next;
                            })} />
                        </td>
                      )}
                      <td className="px-3 py-1 text-center font-semibold" style={{ color: sr.rank === 1 ? '#d97706' : sr.rank === 2 ? '#6b7280' : sr.rank === 3 ? '#92400e' : '#333' }}>
                        {sr.rank}
                      </td>
                      <td className="px-4 py-1 font-medium">
                        <div className="flex items-center gap-1">
                          {expandedStudent === sr.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                          {sr.firstName} {sr.lastName}
                        </div>
                      </td>
                      <td className="px-3 py-1 text-center">{sr.rollNumber || '—'}</td>
                      {examSubjects.map(sub => {
                        const m = sr.subjectMap[sub.id];
                        return (
                          <td key={sub.id} className="px-3 py-1 text-center">
                            {m ? <span style={{ color: (m.marksObtained / m.maxMarks) < 0.33 ? '#dc2626' : '#333' }}>{m.marksObtained}/{m.maxMarks}</span> : '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-center font-semibold">{sr.total}/{sr.maxTotal}</td>
                      <td className="px-3 py-1 text-center" style={{ color: barColor(sr.pct) }}>{sr.pct}%</td>
                      <td className="px-3 py-1 text-center font-bold" style={{ color: gradeColor(sr.grade?.grade) }}>{sr.grade?.grade || '—'}</td>
                      <td className="px-3 py-1 text-center font-bold" style={{ color: sr.passed ? '#16a34a' : '#dc2626' }}>{sr.passed ? 'PASS' : 'FAIL'}</td>
                      {isAdmin && (() => {
                        const due = studentFeeDue(sr);
                        return (
                          <td className="px-3 py-1 text-center font-semibold" style={{ color: due > 0 ? '#dc2626' : '#16a34a' }}>
                            {due > 0 ? formatCurrency(due) : '—'}
                          </td>
                        );
                      })()}
                      {isAdmin && (
                        <td className="px-3 py-1 text-center">
                          {isResultPublished(selectedExam, sr.id)
                            ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Published</span>
                            : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not Published</span>}
                        </td>
                      )}
                      {!isParentRole && (
                        <td className="px-3 py-1 text-center" onClick={e => e.stopPropagation()}>
                          <button onClick={() => printReportCard(sr)} className="p-1 hover:bg-purple-50 rounded text-purple-600" title="Print Report Card">
                            <Printer size={13}/>
                          </button>
                        </td>
                      )}
                    </tr>
                    {/* Expanded subject detail */}
                    {expandedStudent === sr.id && (
                      <tr>
                        <td colSpan={6 + examSubjects.length + (isAdmin ? 3 : 0)} className="px-6 py-3 bg-purple-50 border-b border-purple-100">
                          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(examSubjects.length, 4)}, 1fr)` }}>
                            {examSubjects.map(sub => {
                              const m = sr.subjectMap[sub.id];
                              if (!m) return null;
                              const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
                              const grade = calculateGrade(m.marksObtained, m.maxMarks);
                              return (
                                <div key={sub.id} className="bg-white rounded-lg px-3 py-2 border border-purple-100">
                                  <div className="font-semibold mb-1">{sub.name}</div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span>{m.marksObtained}/{m.maxMarks}</span>
                                    <span className="font-bold" style={{ color: gradeColor(grade.grade) }}>{grade.grade}</span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: barColor(pct) }}/>
                                  </div>
                                  <div style={{ color: barColor(pct) }} className="mt-0.5">{pct}%</div>
                                  {m.remarks && <div className="mt-0.5 italic">{m.remarks}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* -- SUBJECT VIEW -- */}
      {view === 'subject' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {!selectedSubject ? (
            <div className="py-12 text-center">Select a subject to view subject-wise analysis.</div>
          ) : subjectResults.length === 0 ? (
            <div className="py-12 text-center">No marks found for this subject.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{selSubjectObj?.name}</span>
                  <span className="ml-2">— Subject Analysis</span>
                </div>
                <div className="flex gap-4">
                  <span>Avg: <strong style={{ color: barColor(Math.round(subjectResults.reduce((s, r) => s + r.pct, 0) / subjectResults.length)) }}>
                    {Math.round(subjectResults.reduce((s, r) => s + r.pct, 0) / subjectResults.length)}%
                  </strong></span>
                  <span>Highest: <strong style={{ color: '#16a34a' }}>{subjectResults[0]?.marksObtained}/{subjectResults[0]?.maxMarks}</strong></span>
                  <span>Pass: <strong style={{ color: '#16a34a' }}>{subjectResults.filter(r => r.pct >= 33).length}/{subjectResults.length}</strong></span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-3 py-2 text-center font-semibold w-12">Rank</th>
                    <th className="px-4 py-2 text-left font-semibold">Student</th>
                    <th className="px-3 py-2 text-center font-semibold">Class</th>
                    <th className="px-3 py-2 text-center font-semibold">Obtained</th>
                    <th className="px-3 py-2 text-center font-semibold">Max</th>
                    <th className="px-3 py-2 text-center font-semibold">%</th>
                    <th className="px-3 py-2 text-center font-semibold">Progress</th>
                    <th className="px-3 py-2 text-center font-semibold">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectResults.map(sr => {
                    const c = classes.find(c => c.id === sr.classId);
                    return (
                      <tr key={sr.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1 text-center font-semibold" style={{ color: sr.rank === 1 ? '#d97706' : '#333' }}>{sr.rank}</td>
                        <td className="px-4 py-1 font-medium">{sr.firstName} {sr.lastName}</td>
                        <td className="px-3 py-1 text-center">{c ? `${c.name}-${c.section}` : '—'}</td>
                        <td className="px-3 py-1 text-center font-semibold">{sr.marksObtained}</td>
                        <td className="px-3 py-1 text-center">{sr.maxMarks}</td>
                        <td className="px-3 py-1 text-center" style={{ color: barColor(sr.pct) }}>{sr.pct}%</td>
                        <td className="px-3 py-1">
                          <div className="w-24 bg-gray-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full" style={{ width: `${sr.pct}%`, background: barColor(sr.pct) }}/>
                          </div>
                        </td>
                        <td className="px-3 py-1 text-center font-bold" style={{ color: gradeColor(sr.grade?.grade) }}>{sr.grade?.grade || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
export default Results;
